<template>
  <page-layout
      :title="pageTitle"
      :breadcrumb="pageBreadcrumb"
      :status="pageStatus"
      :code="pageCode"
  >
    <template #actions>
      <md-button class="md-success md-round" @click="save" :disabled="isSaving">
        <md-icon>{{ isSaving ? 'hourglass_empty' : 'save' }}</md-icon>
        {{ isSaving ? 'Saving...' : 'Save Changes' }}
      </md-button>
      <md-button class="md-danger md-round" @click="goBack">
        <md-icon>close</md-icon>
        Cancel
      </md-button>
    </template>

    <template #content>
      <div class="edit-form">
        <!-- Payment Details -->
        <div class="form-card">
          <div class="form-card-header">
            <md-icon>receipt_long</md-icon>
            <h3>Payment Details</h3>
          </div>
          <div class="form-card-body">
            <div class="md-layout md-gutter">
              <div class="md-layout-item md-size-50 md-small-size-100">
                <md-field>
                  <label>Payment Date</label>
                  <md-input type="date" v-model="form.payment_date" />
                </md-field>
              </div>

              <div class="md-layout-item md-size-50 md-small-size-100">
                <md-field>
                  <label>Period Start</label>
                  <md-input type="date" v-model="form.start_date" disabled />
                </md-field>
              </div>

              <div class="md-layout-item md-size-50 md-small-size-100">
                <md-field>
                  <label>Period End</label>
                  <md-input type="date" v-model="form.end_date" disabled />
                </md-field>
              </div>

              <div class="md-layout-item md-size-50 md-small-size-100">
                <md-field>
                  <label>Total Amount</label>
                  <md-input :value="formatCurrency(form.total_amount)" disabled />
                </md-field>
              </div>

              <div class="md-layout-item md-size-100">
                <md-field>
                  <label>Notes</label>
                  <md-textarea v-model="form.notes" rows="3"></md-textarea>
                </md-field>
              </div>
            </div>
          </div>
        </div>

        <!-- Labourer Information (Read-only) -->
        <div class="form-card">
          <div class="form-card-header">
            <md-icon>person</md-icon>
            <h3>Labourer (Read-only)</h3>
          </div>
          <div class="form-card-body">
            <div class="md-layout md-gutter">
              <div class="md-layout-item md-size-50 md-small-size-100">
                <div class="readonly-field">
                  <label>Labourer Name</label>
                  <div class="readonly-value">{{ getLabourerName() }}</div>
                </div>
              </div>

              <div class="md-layout-item md-size-50 md-small-size-100">
                <div class="readonly-field">
                  <label>Employee Code</label>
                  <div class="readonly-value">{{ getLabourerCode() }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Items Summary (Read-only) -->
        <div class="form-card">
          <div class="form-card-header">
            <md-icon>list_alt</md-icon>
            <h3>Payment Items ({{ paymentItems.length }})</h3>
          </div>
          <div class="form-card-body">
            <div class="items-summary">
              <div class="summary-item">
                <span class="summary-label">Total Items</span>
                <span class="summary-value">{{ paymentItems.length }}</span>
              </div>
              <div class="summary-item">
                <span class="summary-label">Total Hours</span>
                <span class="summary-value">{{ form.total_hours || 0 }} hrs</span>
              </div>
              <div class="summary-item">
                <span class="summary-label">Total Amount</span>
                <span class="summary-value success">{{ formatCurrency(form.total_amount) }}</span>
              </div>
            </div>

            <div class="items-list">
              <div v-for="(item, index) in paymentItems" :key="item.id" class="item-row">
                <span class="item-index">{{ index + 1 }}</span>
                <span class="item-date">{{ formatDate(item.shift_date) }}</span>
                <span class="item-shift">{{ item.shift_name || 'N/A' }}</span>
                <span class="item-hours">{{ item.hours_worked || 0 }} hrs</span>
                <span class="item-amount">{{ formatCurrency(item.amount) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Error Display -->
        <div v-if="errorMessage" class="error-banner">
          <md-icon>error</md-icon>
          <span>{{ errorMessage }}</span>
        </div>
      </div>
    </template>
  </page-layout>
</template>

<script>
import PageLayout from '@/components/Layouts/PageLayout.vue';
import PageMixin from '@/mixins/page-mixin';
import Swal from 'sweetalert2';
import moment from 'moment';

export default {
  name: 'EditLabourerPaymentPage',
  components: {
    PageLayout: PageLayout
  },
  mixins: [PageMixin],
  data: function () {
    return {
      isSaving: false,
      errorMessage: null,
      paymentItems: [],
      originalPayment: null,
      form: {
        id: null,
        payment_date: null,
        start_date: null,
        end_date: null,
        total_amount: 0,
        total_hours: 0,
        notes: null,
        labourer: null
      }
    };
  },
  computed: {
    pageTitle: function () {
      return 'Edit Payment' + (this.originalPayment ? ' - ' + this.originalPayment.payment_number : '');
    },
    pageBreadcrumb: function () {
      var number = this.originalPayment ? this.originalPayment.payment_number : 'Payment';
      return [
        { label: 'Labourer Payments', path: '/labourer-payments' },
        { label: number, path: '/labourer-payments/' + this.form.id },
        { label: 'Edit' }
      ];
    },
    pageStatus: function () {
      return {
        label: 'Editing',
        type: 'warning'
      };
    },
    pageCode: function () {
      return this.originalPayment ? 'Payment #' + this.originalPayment.payment_number : null;
    }
  },
  created: function () {
    this.get();
  },
  methods: {
    async get() {
      try {
        var id = this.$route.params.id;
        await this.$store.dispatch('labourerPayments/view', { id: id });
        var payment = this.$store.getters['labourerPayments/labourerPayment'];

        if (payment) {
          this.originalPayment = payment;
          this.form = {
            id: payment.id,
            payment_date: this.toDateInput(payment.payment_date),
            start_date: this.toDateInput(payment.start_date),
            end_date: this.toDateInput(payment.end_date),
            total_amount: payment.total_amount || 0,
            total_hours: payment.total_hours || 0,
            notes: payment.notes || '',
            labourer: payment.labourer || null
          };
          this.paymentItems = payment.items || [];
        }
      } catch (error) {
        console.error('Error fetching payment:', error);
        this.$store.dispatch('alerts/error', 'Failed to load payment');
      }
    },

    async save() {
      this.errorMessage = null;
      this.isSaving = true;

      try {
        var payload = {
          id: this.form.id,
          type: 'labourerPayments',
          payment_date: this.toDateTime(this.form.payment_date),
          notes: this.form.notes
        };

        await this.$store.dispatch('labourerPayments/update', payload);

        await Swal.fire({
          title: 'Saved!',
          text: 'Payment updated successfully.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });

        this.$store.dispatch('alerts/success', 'Payment updated successfully');
        this.goBack();
      } catch (error) {
        console.error('Error saving payment:', error);

        if (error.response && error.response.data && error.response.data.errors) {
          var errors = error.response.data.errors;
          if (Array.isArray(errors) && errors.length > 0) {
            this.errorMessage = errors[0].detail || errors[0].title || 'Validation error';
          } else {
            this.errorMessage = 'Validation error occurred';
          }
        } else {
          this.errorMessage = 'Failed to save payment. Please try again.';
        }

        this.$store.dispatch('alerts/error', this.errorMessage);
      } finally {
        this.isSaving = false;
      }
    },

    goBack: function () {
      this.$router.push({ name: 'View Labourer Payment', params: { id: this.form.id } });
    },

    getLabourerName: function () {
      if (this.form.labourer) {
        return this.form.labourer.full_name || this.form.labourer.name || 'Unknown';
      }
      return 'N/A';
    },

    getLabourerCode: function () {
      if (this.form.labourer) {
        return this.form.labourer.emp_code || this.form.labourer.labourer_number || 'N/A';
      }
      return 'N/A';
    },

    toDateInput: function (date) {
      if (!date) return null;
      return moment(date).format('YYYY-MM-DD');
    },

    toDateTime: function (date) {
      if (!date) return null;
      return moment(date).format('YYYY-MM-DD HH:mm:ss');
    },

    formatDate: function (date) {
      if (!date) return 'N/A';
      return moment(date).format('DD MMM YYYY');
    },

    formatCurrency: function (value) {
      if (!value) return 'TZS 0';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'TZS',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    }
  }
};
</script>

<style lang="scss" scoped>
.edit-form {
  padding: 0;
}

.form-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  margin-bottom: 20px;
}

.form-card-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  border-bottom: 2px solid $border-color;
  background: #fafbfc;

  .md-icon {
    color: $brand-primary;
    font-size: 22px !important;
    width: 22px !important;
    height: 22px !important;
  }

  h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: $text-primary;
  }
}

.form-card-body {
  padding: 20px;
}

.readonly-field {
  margin-bottom: 16px;

  label {
    display: block;
    font-size: 12px;
    color: $text-muted;
    text-transform: uppercase;
    font-weight: 600;
    letter-spacing: 0.5px;
    margin-bottom: 6px;
  }
}

.readonly-value {
  font-size: 14px;
  color: $text-primary;
  font-weight: 500;
  padding: 10px 12px;
  background: #f8f9fa;
  border-radius: 6px;
  border-left: 3px solid $brand-primary;
}

.items-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.summary-item {
  text-align: center;
  padding: 14px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 3px solid $brand-primary;
}

.summary-label {
  display: block;
  font-size: 11px;
  color: $text-muted;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  margin-bottom: 4px;
}

.summary-value {
  display: block;
  font-size: 18px;
  font-weight: 700;
  color: $text-primary;

  &.success {
    color: $brand-success;
  }
}

.items-list {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid $border-color;
  border-radius: 8px;
}

.item-row {
  display: grid;
  grid-template-columns: 40px 120px 1fr 100px 120px;
  gap: 12px;
  align-items: center;
  padding: 10px 16px;
  border-bottom: 1px solid #f5f5f5;
  font-size: 13px;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f8f9fa;
  }
}

.item-index {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #e8eaf6;
  color: $brand-primary;
  font-weight: 600;
  font-size: 11px;
}

.item-date {
  color: $text-secondary;
}

.item-shift {
  color: $text-primary;
  font-weight: 500;
}

.item-hours {
  color: $text-secondary;
  text-align: right;
}

.item-amount {
  color: $brand-success;
  font-weight: 600;
  text-align: right;
}

.error-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: #ffebee;
  border-radius: 8px;
  color: #c62828;
  font-size: 13px;
  margin-top: 16px;

  .md-icon {
    color: #c62828;
  }
}

@media (max-width: 768px) {
  .items-summary {
    grid-template-columns: 1fr;
  }

  .item-row {
    grid-template-columns: 1fr;
    gap: 6px;
  }

  .item-hours,
  .item-amount {
    text-align: left;
  }
}
</style>